import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, AlertTriangle, Users, Lock, Cloud, ChevronDown, ChevronUp, Check, Zap, X } from 'lucide-react';
import VaultoLogo from './VaultoLogo';

export default function LandingPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [hasShownPopup, setHasShownPopup] = useState(false);
  const navigate = useNavigate();

  // Sticky CTA bar on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Exit-intent popup
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY < 10 && !hasShownPopup) {
        setShowExitPopup(true);
        setHasShownPopup(true);
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [hasShownPopup]);

  const handleCTA = () => {
    navigate('/register');
  };

  const stats = [
    { number: "1 in 5", label: "accounts will be compromised", source: "Google Security Report, 2023-2026" },
    { number: "22M", label: "hacking incidents last year", source: "Industry Reports" },
    { number: "17.5M", label: "accounts exposed this month", source: "Malwarebytes, Jan 2026" },
    { number: "10 min", label: "an influencer hacked every", source: "2022-2025 Studies" },
  ];

  const newsArticles = [
    {
      headline: "17.5 Million Instagram Accounts Exposed in Major Data Leak",
      source: "Engadget",
      date: "January 2026",
      tag: "BREAKING",
      tagColor: "#ec4899",
      description: "Usernames, emails, phone numbers, and home addresses leaked on dark web. Users flooded with password reset attacks.",
      isMain: true
    },
    {
      headline: "Instagram Users Complain of Mass Suspensions of Accounts, Likely By AI",
      source: "SFist",
      date: "June 2025",
      tag: "TRENDING",
      tagColor: "#f472b6",
      description: "Thousands of accounts wrongfully disabled overnight with no warning or explanation."
    },
    {
      headline: "The Great Meta Ban Wave 2025: Instagram Accounts Caught in the Crossfire",
      source: "Medium",
      date: "October 2025",
      tag: "INVESTIGATION",
      tagColor: "#a855f7",
      description: "Creators, businesses, and verified accounts banned without warning. Appeals process described as 'shouting into a void.'"
    },
    {
      headline: "Instagram Influencer's Account Hacked to Trick Users and Steal Banking Credentials",
      source: "Cyberpress",
      date: "May 2025",
      tag: "SECURITY",
      tagColor: "#ec4899",
      description: "SIM-swapping combined with AI deepfakes used to scam followers after account takeover."
    },
    {
      headline: "Selena Gomez's Instagram Was Hacked",
      source: "Entertainment Tonight",
      date: "2017",
      tag: "CELEBRITY",
      tagColor: "#8b5cf6",
      description: "The #1 most-followed account on Instagram (125M followers) was compromised. No one is safe."
    }
  ];

  const quotes = [
    {
      text: "My 1.2M-follower account was banned overnight — I lost $40K in sponsorships and 4 years of content.",
      author: "Fashion Creator",
      platform: "Reddit",
      link: "#"
    },
    {
      text: "This ban has directly affected my business and all of the hard work and branding that I've spent countless hours pouring into my business, my gym, and my students.",
      author: "Gym Owner",
      platform: "Reddit",
      link: "#"
    },
    {
      text: "Me being told I'm not really me was never in my 2025 bingo cards. This is draconian in its purest form.",
      author: "Stanley Roberts",
      platform: "Bay Area Journalist",
      link: "#"
    },
    {
      text: "The Meta support chat sends me in endless loops of broken links... then they just close the ticket and say 'We've given you all the resources you need' and never actually help.",
      author: "Business Owner",
      platform: "Multiple Banned Accounts",
      link: "#"
    }
  ];

  // Updated with actual video files
  const videoTestimonials = [
    {
      name: "Sarah",
      handle: "@sarahcreates",
      followers: "245K followers",
      quote: "I almost lost 4 years of content. Now I sleep easy knowing everything is backed up.",
      video: "/vaulto_testamonial_sarah.mp4"
    },
    {
      name: "Alex",
      handle: "@alexdigital",
      followers: "89K followers",
      quote: "My friend lost her account last month. That's when I knew I needed Vaulto.",
      video: "/vaulto_testamonial_alex.mp4"
    },
    {
      name: "Ciara",
      handle: "@ciarastyle",
      followers: "512K followers",
      quote: "As a full-time creator, my Instagram IS my business. Vaulto is my insurance policy.",
      video: "/vaulto_testamonial_ciara.mp4"
    },
    {
      name: "Elio",
      handle: "@eliotravels",
      followers: "178K followers",
      quote: "Setup took 2 minutes. Now I get daily backups without thinking about it.",
      video: "/vaulto_testamonial_elio.mp4"
    }
  ];

  const faqs = [
    {
      question: "Is Vaulto safe to use? Will it get my account banned?",
      answer: "Absolutely safe. Vaulto uses Meta's Official API—the same technology Instagram provides for authorized third-party apps. We're fully compliant with Instagram's terms of service. Your account will never be flagged or banned for using Vaulto."
    },
    {
      question: "Can Vaulto recover a deleted account?",
      answer: "No — but it lets you rebuild instantly with full backups. If your account is banned or deleted, you'll have all your content saved and can quickly restart on a new account with years of work preserved."
    },
    {
      question: "Is it really free?",
      answer: "Yes — generous free plan forever. You can backup one account completely free, indefinitely. Pro plan unlocks unlimited accounts and priority support."
    },
    {
      question: "What exactly gets backed up?",
      answer: "Vaulto backs up your posts (photos and videos), Reels, Stories archive, captions, and metadata. Everything that makes your content valuable is preserved."
    },
    {
      question: "How often does Vaulto back up my content?",
      answer: "Vaulto runs automatic daily backups. Every new post, reel, or story is captured without you lifting a finger. You're always protected."
    },
    {
      question: "Do you store my content on your servers?",
      answer: "No. Your backups go directly to YOUR Google Drive. You own it, you control it. Vaulto never stores your content on third-party servers."
    },
    {
      question: "Can I backup multiple Instagram accounts?",
      answer: "Yes! Whether you manage personal accounts, business profiles, or client accounts, Vaulto can protect them all with Pro plan."
    },
    {
      question: "What if my account is already disabled—can Vaulto help?",
      answer: "Vaulto is a preventive solution. If your account is already disabled, we can't recover past content. That's exactly why you should start backing up now—before it's too late."
    },
    {
      question: "How is this different from Instagram's native data download?",
      answer: "Instagram's download is manual, one-time, and tedious. Vaulto is automatic, daily, and effortless. Plus, your backups are organized and accessible in your Google Drive anytime."
    },
    {
      question: "Is my backup private? Can anyone else see it?",
      answer: "Completely private. Backups are stored in your personal Google Drive with your privacy settings. Only you have access."
    }
  ];

  // Video Testimonial Component
  const VideoTestimonial = ({ testimonial }) => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const togglePlay = () => {
      if (videoRef.current) {
        if (isPlaying) {
          videoRef.current.pause();
        } else {
          videoRef.current.play();
        }
        setIsPlaying(!isPlaying);
      }
    };

    return (
      <div className="video-card" style={{
        width: '100%',
        maxWidth: '280px',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(168, 85, 247, 0.2)',
        borderRadius: '20px',
        overflow: 'hidden',
        cursor: 'pointer',
        backdropFilter: 'blur(10px)'
      }}>
        {/* Video Container - 9:16 aspect ratio */}
        <div 
          onClick={togglePlay}
          style={{
            width: '100%',
            aspectRatio: '9/16',
            position: 'relative',
            background: 'linear-gradient(180deg, rgba(168, 85, 247, 0.15) 0%, rgba(236, 72, 153, 0.1) 100%)'
          }}
        >
          <video
            ref={videoRef}
            src={testimonial.video}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            playsInline
            loop
            muted
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
          
          {/* Play Button Overlay */}
          {!isPlaying && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
            }}>
              <div className="play-button" style={{
                width: '72px',
                height: '72px',
                background: 'rgba(168, 85, 247, 0.6)',
                border: '2px solid rgba(168, 85, 247, 0.8)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}>
                <div style={{
                  width: 0,
                  height: 0,
                  borderLeft: '20px solid #fff',
                  borderTop: '12px solid transparent',
                  borderBottom: '12px solid transparent',
                  marginLeft: '4px'
                }} />
              </div>
            </div>
          )}

          {/* User info overlay */}
          <div style={{
            position: 'absolute',
            bottom: '16px',
            left: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #ec4899, #a855f7)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: '700'
            }}>
              {testimonial.name.charAt(0)}
            </div>
            <div>
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#fff',
                textShadow: '0 2px 4px rgba(0,0,0,0.5)'
              }}>
                {testimonial.name}
              </div>
              <div style={{
                fontSize: '11px',
                color: 'rgba(255,255,255,0.8)',
                textShadow: '0 2px 4px rgba(0,0,0,0.5)'
              }}>
                {testimonial.handle}
              </div>
            </div>
          </div>
        </div>

        {/* Info section */}
        <div style={{ padding: '20px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px'
          }}>
            <Users size={14} color="#a855f7" />
            <span style={{
              fontSize: '13px',
              color: '#c084fc',
              fontWeight: '500'
            }}>
              {testimonial.followers}
            </span>
          </div>
          <p style={{
            fontSize: '14px',
            color: 'rgba(255,255,255,0.8)',
            lineHeight: '1.5',
            fontStyle: 'italic',
            margin: 0
          }}>
            "{testimonial.quote}"
          </p>
        </div>
      </div>
    );
  };

  const CTABox = ({ text = "Start Protecting Your Content" }) => {
    return (
      <div className="inline-cta" style={{
        background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
        border: '1px solid rgba(236, 72, 153, 0.2)',
        borderRadius: '16px',
        padding: '32px',
        textAlign: 'center',
        marginTop: '48px'
      }}>
        <p style={{
          fontSize: '18px',
          fontWeight: '600',
          marginBottom: '20px',
          background: 'linear-gradient(135deg, #ec4899, #a855f7)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Ready to protect your content?
        </p>
        <button onClick={handleCTA} className="cta-button" style={{
          background: 'linear-gradient(135deg, #ec4899, #a855f7)',
          border: 'none',
          borderRadius: '12px',
          padding: '16px 36px',
          fontSize: '16px',
          fontWeight: '700',
          color: '#fff',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          fontFamily: 'inherit'
        }}>
          <Shield size={18} />
          {text}
        </button>
        <p style={{
          marginTop: '12px',
          fontSize: '13px',
          color: 'rgba(255,255,255,0.5)'
        }}>
          Free to start • 60 seconds • Official Meta API
        </p>
      </div>
    );
  };

  return (
    <div style={{
      fontFamily: "'Inter', 'Outfit', sans-serif",
      background: 'linear-gradient(135deg, #1e1b4b 0%, #581c87 50%, #831843 100%)',
      color: '#ffffff',
      minHeight: '100vh',
      overflow: 'hidden'
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        .landing-page * {
          box-sizing: border-box;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        @keyframes slideIn {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }

        .animate-slide-up {
          animation: slideUp 0.8s ease-out forwards;
        }

        .delay-1 { animation-delay: 0.1s; opacity: 0; }
        .delay-2 { animation-delay: 0.2s; opacity: 0; }
        .delay-3 { animation-delay: 0.3s; opacity: 0; }
        .delay-4 { animation-delay: 0.4s; opacity: 0; }
        .delay-5 { animation-delay: 0.5s; opacity: 0; }

        .news-card {
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .news-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(236, 72, 153, 0.2);
        }

        .cta-button {
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .cta-button:hover {
          transform: scale(1.05);
          box-shadow: 0 20px 40px rgba(236, 72, 153, 0.4);
        }

        .cta-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.5s ease;
        }

        .cta-button:hover::before {
          left: 100%;
        }

        .stat-card {
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: scale(1.05);
          background: rgba(236, 72, 153, 0.15);
        }

        .faq-item {
          transition: all 0.3s ease;
        }

        .quote-card {
          transition: all 0.3s ease;
        }

        .quote-card:hover {
          transform: translateX(8px);
          border-left-color: #ec4899;
        }

        .video-card {
          transition: all 0.3s ease;
        }

        .video-card:hover {
          transform: scale(1.03);
          box-shadow: 0 20px 40px rgba(168, 85, 247, 0.2);
        }

        .video-card:hover .play-button {
          transform: scale(1.1);
          background: linear-gradient(135deg, #ec4899, #a855f7);
        }

        .inline-cta {
          transition: all 0.3s ease;
        }

        .inline-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(236, 72, 153, 0.3);
        }

        .sticky-bar {
          animation: slideIn 0.3s ease-out;
        }

        .exit-popup-overlay {
          animation: fadeIn 0.3s ease-out;
        }

        .exit-popup {
          animation: scaleIn 0.3s ease-out;
        }

        .fomo-text {
          color: #f472b6;
          font-size: 13px;
          margin-top: 8px;
          font-style: italic;
        }
      `}</style>

      {/* Exit Intent Popup */}
      {showExitPopup && (
        <>
          <div className="exit-popup-overlay" onClick={() => setShowExitPopup(false)} style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            zIndex: 2000
          }} />
          <div className="exit-popup" style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'linear-gradient(135deg, #1e1b4b 0%, #581c87 100%)',
            border: '2px solid rgba(236, 72, 153, 0.5)',
            borderRadius: '24px',
            padding: '48px',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center',
            zIndex: 2001,
            boxShadow: '0 40px 80px rgba(0,0,0,0.5)'
          }}>
            <button onClick={() => setShowExitPopup(false)} style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              padding: '8px'
            }}>
              <X size={24} />
            </button>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(236, 72, 153, 0.3)',
              borderRadius: '100px',
              padding: '8px 16px',
              marginBottom: '24px',
              fontSize: '14px',
              color: '#f9a8d4'
            }}>
              <AlertTriangle size={16} />
              WAIT!
            </div>
            <h3 style={{
              fontSize: '28px',
              fontWeight: '700',
              marginBottom: '16px',
              lineHeight: '1.3'
            }}>
              17.5M accounts just got exposed
            </h3>
            <p style={{
              fontSize: '18px',
              color: 'rgba(255,255,255,0.7)',
              marginBottom: '32px',
              lineHeight: '1.6'
            }}>
              Back up free in 60 seconds.<br />
              Don't become a statistic.
            </p>
            <button onClick={handleCTA} className="cta-button" style={{
              background: 'linear-gradient(135deg, #ec4899, #a855f7)',
              border: 'none',
              borderRadius: '12px',
              padding: '18px 40px',
              fontSize: '18px',
              fontWeight: '700',
              color: '#fff',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              fontFamily: 'inherit'
            }}>
              <Shield size={20} />
              Protect My Account Now
            </button>
          </div>
        </>
      )}

      {/* Sticky CTA Bar */}
      {showStickyBar && (
        <div className="sticky-bar" style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(135deg, rgba(30, 27, 75, 0.98) 0%, rgba(88, 28, 135, 0.98) 100%)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(236, 72, 153, 0.3)',
          padding: '16px 20px',
          zIndex: 1500,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '20px'
        }}>
          <span style={{
            color: 'rgba(255,255,255,0.8)',
            fontSize: '15px',
            fontWeight: '500'
          }}>
            🛡️ Free Forever Plan Available
          </span>
          <button onClick={handleCTA} style={{
            background: 'linear-gradient(135deg, #ec4899, #a855f7)',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: '700',
            color: '#fff',
            cursor: 'pointer',
            fontFamily: 'inherit'
          }}>
            Protect Now — It's Free
          </button>
        </div>
      )}

      {/* Header */}
      <header style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: 'rgba(30, 27, 75, 0.9)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <VaultoLogo className="h-10 w-auto" showText={true} />
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <Link to="/pricing" style={{
              color: 'rgba(255, 255, 255, 0.7)',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'color 0.2s'
            }}>
              Pricing
            </Link>
            <Link to="/login" style={{
              color: 'rgba(255, 255, 255, 0.7)',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'color 0.2s'
            }}>
              Sign In
            </Link>
            <Link to="/register" style={{
              background: 'linear-gradient(135deg, #ec4899, #a855f7)',
              color: '#fff',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '600',
              padding: '10px 20px',
              borderRadius: '8px',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}>
              Get Started Free
            </Link>
          </div>
        </div>
      </header>

      <div className="landing-page">
        {/* Decorative background elements */}
        <div style={{
          position: 'fixed',
          top: '10%',
          left: '5%',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(168, 85, 247, 0.3) 0%, transparent 70%)',
          filter: 'blur(80px)',
          pointerEvents: 'none'
        }} />
        <div style={{
          position: 'fixed',
          top: '40%',
          right: '5%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(236, 72, 153, 0.2) 0%, transparent 70%)',
          filter: 'blur(100px)',
          pointerEvents: 'none'
        }} />
        <div style={{
          position: 'fixed',
          bottom: '10%',
          left: '30%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none'
        }} />

        {/* Hero Section */}
        <section style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '80px 20px 40px',
          position: 'relative'
        }}>
          <div style={{
            maxWidth: '900px',
            textAlign: 'center',
            position: 'relative',
            zIndex: 10
          }}>
            {/* Alert Badge */}
            <div className={`animate-slide-up delay-1`} style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(236, 72, 153, 0.2)',
              border: '1px solid rgba(236, 72, 153, 0.4)',
              borderRadius: '100px',
              padding: '8px 20px',
              marginBottom: '32px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#f9a8d4'
            }}>
              <span style={{ 
                width: '8px', 
                height: '8px', 
                background: '#ec4899', 
                borderRadius: '50%',
                animation: 'pulse 2s infinite'
              }} />
              JANUARY 2026: 17.5 MILLION ACCOUNTS EXPOSED
            </div>

            {/* Main Headline */}
            <h1 className={`animate-slide-up delay-2`} style={{
              fontSize: 'clamp(42px, 8vw, 80px)',
              fontWeight: '800',
              lineHeight: '1.05',
              marginBottom: '24px',
              letterSpacing: '-0.03em'
            }}>
              Your Instagram
              <br />
              <span style={{
                background: 'linear-gradient(135deg, #f472b6, #c084fc)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Could Vanish</span>
              <br />
              Tonight.
            </h1>

            {/* Subheadline */}
            <p className={`animate-slide-up delay-3`} style={{
              fontSize: 'clamp(18px, 2.5vw, 24px)',
              color: 'rgba(255,255,255,0.7)',
              maxWidth: '600px',
              margin: '0 auto 40px',
              lineHeight: '1.6',
              fontWeight: '400'
            }}>
              One hack. One wrongful ban. One AI glitch.
              <br />
              <strong style={{ color: '#fff' }}>Years of content, revenue, memories — erased in seconds.</strong>
            </p>

            {/* CTA Button */}
            <button onClick={handleCTA} className={`cta-button animate-slide-up delay-4`} style={{
              background: 'linear-gradient(135deg, #ec4899, #a855f7)',
              border: 'none',
              borderRadius: '12px',
              padding: '20px 48px',
              fontSize: '18px',
              fontWeight: '700',
              color: '#fff',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              fontFamily: 'inherit'
            }}>
              <Shield size={22} />
              Protect My Content Before It's Gone
            </button>

            <p className={`animate-slide-up delay-5`} style={{
              marginTop: '16px',
              fontSize: '14px',
              color: 'rgba(255,255,255,0.5)'
            }}>
              Meta & Google API approved • Automatic backup to your Google Drive • Free plan included
            </p>
          </div>

          {/* Scroll indicator */}
          <div style={{
            position: 'absolute',
            bottom: '40px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            color: 'rgba(255,255,255,0.4)',
            fontSize: '12px',
            letterSpacing: '2px'
          }}>
            <span>SCROLL</span>
            <ChevronDown size={20} style={{ animation: 'pulse 2s infinite' }} />
          </div>
        </section>

        {/* Statistics Section */}
        <section style={{
          padding: '100px 20px',
          background: 'linear-gradient(180deg, rgba(88, 28, 135, 0.3) 0%, rgba(30, 27, 75, 0.5) 100%)',
          position: 'relative'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 style={{
                fontSize: 'clamp(14px, 2vw, 16px)',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #f472b6, #c084fc)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '3px',
                marginBottom: '16px'
              }}>
                NUMBERS THAT SHOULD TERRIFY EVERY CREATOR
              </h2>
              <h3 style={{
                fontSize: 'clamp(32px, 5vw, 48px)',
                fontWeight: '700',
                lineHeight: '1.2'
              }}>
                It's More Common Than You Think
              </h3>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '24px'
            }}>
              {stats.map((stat, index) => (
                <div key={index} className="stat-card" style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '16px',
                  padding: '32px',
                  textAlign: 'center',
                  backdropFilter: 'blur(10px)'
                }}>
                  <div style={{
                    fontSize: 'clamp(40px, 6vw, 56px)',
                    fontWeight: '800',
                    background: 'linear-gradient(135deg, #f472b6, #c084fc)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    marginBottom: '8px'
                  }}>
                    {stat.number}
                  </div>
                  <div style={{
                    fontSize: '16px',
                    color: 'rgba(255,255,255,0.8)',
                    marginBottom: '12px',
                    fontWeight: '500'
                  }}>
                    {stat.label}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.4)'
                  }}>
                    Source: {stat.source}
                  </div>
                </div>
              ))}
            </div>

            {/* Tagline under stats */}
            <div style={{
              marginTop: '48px',
              textAlign: 'center',
              padding: '24px',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.08)'
            }}>
              <p style={{
                fontSize: '18px',
                color: 'rgba(255,255,255,0.8)',
                fontStyle: 'italic',
                margin: 0,
                lineHeight: '1.6'
              }}>
                "These aren't just numbers. They're someone's business. Someone's memories. Someone's future.
                <br />
                <strong style={{ color: '#f472b6' }}>Don't let it be yours.</strong>"
              </p>
            </div>

            <CTABox text="Start Now — Before It's Too Late" />
          </div>
        </section>

        {/* How It Happens Section */}
        <section style={{
          padding: '100px 20px',
          background: 'rgba(30, 27, 75, 0.5)'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 style={{
                fontSize: 'clamp(14px, 2vw, 16px)',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #f472b6, #c084fc)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '3px',
                marginBottom: '16px'
              }}>
                IT CAN HAPPEN TO ANYONE
              </h2>
              <h3 style={{
                fontSize: 'clamp(32px, 5vw, 48px)',
                fontWeight: '700',
                lineHeight: '1.2'
              }}>
                5 Ways You Lose Everything
              </h3>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {[
                { icon: '🔓', title: 'Your account gets hacked', desc: 'Someone steals your login through phishing, data breaches, or SIM swapping', fomo: 'Every 10 minutes, another influencer is compromised.' },
                { icon: '🤖', title: 'Instagram bans you by mistake', desc: 'Their AI makes errors. Thousands of legitimate accounts disabled overnight.', fomo: 'No warning. No explanation. Just gone.' },
                { icon: '🎯', title: 'Someone mass-reports you', desc: 'Competitors, trolls, anyone with a grudge can weaponize the report system', fomo: 'Competitors can report you 100x in a day — one viral post is enough.' },
                { icon: '📱', title: 'You lose access to your email or phone', desc: 'Change numbers? Forget passwords? Locked out forever.', fomo: '2FA can backfire if you lose your device.' },
                { icon: '💀', title: 'A data breach exposes your info', desc: 'Your details end up on the dark web. You become a target.', fomo: '17.5 million exposed this month alone.' }
              ].map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '20px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  padding: '24px',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(10px)'
                }}>
                  <div style={{
                    fontSize: '32px',
                    width: '60px',
                    height: '60px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2), rgba(168, 85, 247, 0.2))',
                    borderRadius: '12px',
                    flexShrink: 0
                  }}>
                    {item.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      marginBottom: '4px'
                    }}>
                      {item.title}
                    </h4>
                    <p style={{
                      fontSize: '15px',
                      color: 'rgba(255,255,255,0.6)',
                      lineHeight: '1.5',
                      margin: 0
                    }}>
                      {item.desc}
                    </p>
                    <p className="fomo-text">
                      ⚠️ {item.fomo}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              marginTop: '40px',
              padding: '24px',
              background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(168, 85, 247, 0.15))',
              border: '1px solid rgba(236, 72, 153, 0.3)',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <p style={{
                fontSize: '18px',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #f472b6, #c084fc)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '8px'
              }}>
                The Worst Part?
              </p>
              <p style={{
                fontSize: '16px',
                color: 'rgba(255,255,255,0.8)',
                margin: 0
              }}>
                Meta has almost no customer support. Recovery takes <strong>17+ days</strong>—if it works at all.
                <br />
                Most creators <strong>never get their accounts back</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* News Section */}
        <section style={{
          padding: '100px 20px',
          background: 'linear-gradient(180deg, rgba(88, 28, 135, 0.4) 0%, rgba(131, 24, 67, 0.3) 100%)'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 style={{
                fontSize: 'clamp(14px, 2vw, 16px)',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #f472b6, #c084fc)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '3px',
                marginBottom: '16px'
              }}>
                THIS ISN'T FEAR-MONGERING
              </h2>
              <h3 style={{
                fontSize: 'clamp(32px, 5vw, 48px)',
                fontWeight: '700',
                lineHeight: '1.2',
                marginBottom: '16px'
              }}>
                It's Happening Right Now
              </h3>
              <p style={{
                fontSize: '18px',
                color: 'rgba(255,255,255,0.6)',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                Real headlines. Real victims. Real consequences.
              </p>
            </div>

            {/* Main Breaking News */}
            <div className="news-card" style={{
              background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, rgba(168, 85, 247, 0.1) 100%)',
              border: '2px solid rgba(236, 72, 153, 0.4)',
              borderRadius: '20px',
              padding: '40px',
              marginBottom: '32px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: '0',
                right: '0',
                width: '200px',
                height: '200px',
                background: 'radial-gradient(circle, rgba(236, 72, 153, 0.3) 0%, transparent 70%)',
                filter: 'blur(40px)'
              }} />
              
              <div style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg, #ec4899, #a855f7)',
                color: '#fff',
                fontSize: '11px',
                fontWeight: '700',
                padding: '6px 12px',
                borderRadius: '4px',
                marginBottom: '16px',
                letterSpacing: '1px'
              }}>
                🔴 BREAKING — JANUARY 2026
              </div>
              
              <h4 style={{
                fontSize: 'clamp(24px, 4vw, 36px)',
                fontWeight: '700',
                marginBottom: '16px',
                lineHeight: '1.2'
              }}>
                17.5 Million Instagram Accounts Exposed in Major Data Leak
              </h4>
              
              <p style={{
                fontSize: '18px',
                color: 'rgba(255,255,255,0.8)',
                marginBottom: '24px',
                lineHeight: '1.6',
                maxWidth: '800px'
              }}>
                Usernames, emails, phone numbers, and <strong>home addresses</strong> leaked on dark web.
                Users worldwide flooded with password reset attacks. Hackers actively attempting account takeovers.
              </p>
              
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '16px',
                fontSize: '14px',
                color: 'rgba(255,255,255,0.5)'
              }}>
                <span>📰 Engadget</span>
                <span>📰 Digital Trends</span>
                <span>📰 Malwarebytes</span>
                <span>📰 Cyberpress</span>
              </div>
            </div>

            {/* Expert Quote */}
            <div style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderLeft: '4px solid #ec4899',
              borderRadius: '0 12px 12px 0',
              padding: '32px',
              marginBottom: '32px'
            }}>
              <p style={{
                fontSize: '20px',
                fontStyle: 'italic',
                color: 'rgba(255,255,255,0.9)',
                marginBottom: '16px',
                lineHeight: '1.6'
              }}>
                "If you get this message from Instagram and were not expecting it, you have found yourself in the crosshairs of an ongoing account attack."
              </p>
              <div style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.5)'
              }}>
                — <strong style={{ color: '#fff' }}>Davey Winder</strong>, Forbes Cybersecurity Contributor
                <span style={{ color: '#f472b6', marginLeft: '8px' }}>(Received 12 password reset emails in 48 hours)</span>
              </div>
            </div>

            {/* Other News Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              {newsArticles.slice(1).map((article, index) => (
                <div key={index} className="news-card" style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '16px',
                  padding: '28px',
                  display: 'flex',
                  flexDirection: 'column',
                  backdropFilter: 'blur(10px)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}>
                    <span style={{
                      background: article.tagColor,
                      color: '#fff',
                      fontSize: '10px',
                      fontWeight: '700',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      letterSpacing: '1px'
                    }}>
                      {article.tag}
                    </span>
                    <span style={{
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.4)'
                    }}>
                      {article.date}
                    </span>
                  </div>
                  
                  <h4 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    marginBottom: '12px',
                    lineHeight: '1.4',
                    flex: 1
                  }}>
                    {article.headline}
                  </h4>
                  
                  <p style={{
                    fontSize: '14px',
                    color: 'rgba(255,255,255,0.6)',
                    marginBottom: '16px',
                    lineHeight: '1.5'
                  }}>
                    {article.description}
                  </p>
                  
                  <div style={{
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.4)'
                  }}>
                    📰 {article.source}
                  </div>
                </div>
              ))}
            </div>

            <CTABox text="Protect Your Account Now" />
          </div>
        </section>

        {/* Quotes Section */}
        <section style={{
          padding: '100px 20px',
          background: 'rgba(30, 27, 75, 0.6)'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 style={{
                fontSize: 'clamp(14px, 2vw, 16px)',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #f472b6, #c084fc)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '3px',
                marginBottom: '16px'
              }}>
                REAL VICTIMS
              </h2>
              <h3 style={{
                fontSize: 'clamp(32px, 5vw, 48px)',
                fontWeight: '700',
                lineHeight: '1.2'
              }}>
                From People Who Lost It All
              </h3>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {quotes.map((quote, index) => (
                <div key={index} className="quote-card" style={{
                  background: 'rgba(255,255,255,0.03)',
                  borderLeft: '3px solid rgba(168, 85, 247, 0.5)',
                  borderRadius: '0 12px 12px 0',
                  padding: '28px 32px',
                  transition: 'all 0.3s ease'
                }}>
                  <p style={{
                    fontSize: '17px',
                    color: 'rgba(255,255,255,0.85)',
                    marginBottom: '16px',
                    lineHeight: '1.7',
                    fontStyle: 'italic'
                  }}>
                    "{quote.text}"
                  </p>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '12px'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      color: 'rgba(255,255,255,0.5)'
                    }}>
                      — <strong style={{ color: '#fff' }}>{quote.author}</strong>
                      <span style={{ marginLeft: '8px', opacity: 0.7 }}>{quote.platform}</span>
                    </div>
                    <span style={{
                      fontSize: '12px',
                      color: '#a855f7',
                      cursor: 'pointer'
                    }}>
                      Read full story →
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Video Testimonials Section */}
        <section style={{
          padding: '100px 20px',
          background: 'linear-gradient(180deg, rgba(88, 28, 135, 0.5) 0%, rgba(30, 27, 75, 0.6) 100%)',
          position: 'relative'
        }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 10 }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 style={{
                fontSize: 'clamp(14px, 2vw, 16px)',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #f472b6, #c084fc)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '3px',
                marginBottom: '16px'
              }}>
                CREATOR STORIES ★★★★★
              </h2>
              <h3 style={{
                fontSize: 'clamp(32px, 5vw, 48px)',
                fontWeight: '700',
                lineHeight: '1.2',
                marginBottom: '16px'
              }}>
                Trusted by Creators Who
                <br />
                <span style={{
                  background: 'linear-gradient(135deg, #f472b6, #c084fc)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>Refuse to Lose Everything</span>
              </h3>
              <p style={{
                fontSize: '18px',
                color: 'rgba(255,255,255,0.6)',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                Hear from real creators who protect their content with Vaulto.
              </p>
              
              {/* Stat overlay */}
              <div style={{
                marginTop: '24px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(168, 85, 247, 0.2)',
                border: '1px solid rgba(168, 85, 247, 0.4)',
                borderRadius: '100px',
                padding: '10px 20px',
                fontSize: '14px',
                color: '#c084fc',
                fontWeight: '500'
              }}>
                <Shield size={16} />
                10,000+ creators already protected — join them before it's too late
              </div>
            </div>

            {/* Video Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '24px',
              justifyItems: 'center'
            }}>
              {videoTestimonials.map((testimonial, index) => (
                <VideoTestimonial key={index} testimonial={testimonial} />
              ))}
            </div>

            <CTABox text="Join Thousands of Creators" />
          </div>
        </section>

        {/* What You Lose Section */}
        <section style={{
          padding: '100px 20px',
          background: 'rgba(30, 27, 75, 0.7)'
        }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 style={{
                fontSize: 'clamp(14px, 2vw, 16px)',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #f472b6, #c084fc)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '3px',
                marginBottom: '16px'
              }}>
                THINK ABOUT IT
              </h2>
              <h3 style={{
                fontSize: 'clamp(32px, 5vw, 48px)',
                fontWeight: '700',
                lineHeight: '1.2'
              }}>
                What Would You Actually Lose?
              </h3>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '24px'
            }}>
              {[
                {
                  icon: '📸',
                  title: 'Your Content',
                  items: ['Years of photos & reels', 'High-production shoots', 'Unrepeatable moments', 'Stories archive'],
                  loss: '100s of hours to recreate'
                },
                {
                  icon: '💰',
                  title: 'Your Business',
                  items: ['Brand deal proof', 'Sponsorship portfolio', 'Revenue streams', 'Customer relationships'],
                  loss: '$5K-$50K/month in lost deals'
                },
                {
                  icon: '👥',
                  title: 'Your Community',
                  items: ['Followers built over years', 'DM conversations', 'Brand relationships', 'Engagement history'],
                  loss: 'Years of relationship building'
                },
                {
                  icon: '⏰',
                  title: 'Your Time',
                  items: ['Hundreds of hours', 'Creative energy', 'Strategic growth', 'Irreplaceable effort'],
                  loss: 'Time you can never get back'
                }
              ].map((category, index) => (
                <div key={index} style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '16px',
                  padding: '32px',
                  textAlign: 'center',
                  backdropFilter: 'blur(10px)'
                }}>
                  <div style={{
                    fontSize: '40px',
                    marginBottom: '16px'
                  }}>
                    {category.icon}
                  </div>
                  <h4 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    marginBottom: '20px'
                  }}>
                    {category.title}
                  </h4>
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0
                  }}>
                    {category.items.map((item, i) => (
                      <li key={i} style={{
                        fontSize: '14px',
                        color: 'rgba(255,255,255,0.6)',
                        padding: '8px 0',
                        borderBottom: i < category.items.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none'
                      }}>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: 'rgba(236, 72, 153, 0.1)',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: '#f472b6',
                    fontWeight: '500'
                  }}>
                    💸 {category.loss}
                  </div>
                </div>
              ))}
            </div>

            <CTABox text="Don't Risk Losing It All" />
          </div>
        </section>

        {/* Solution Section */}
        <section style={{
          padding: '100px 20px',
          background: 'linear-gradient(180deg, rgba(88, 28, 135, 0.5) 0%, rgba(30, 27, 75, 0.7) 100%)',
          position: 'relative'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 10 }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 style={{
                fontSize: 'clamp(14px, 2vw, 16px)',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #f472b6, #c084fc)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '3px',
                marginBottom: '16px'
              }}>
                THE SOLUTION
              </h2>
              <h3 style={{
                fontSize: 'clamp(32px, 5vw, 48px)',
                fontWeight: '700',
                lineHeight: '1.2',
                marginBottom: '16px'
              }}>
                Protect Everything.
                <br />
                <span style={{
                  background: 'linear-gradient(135deg, #f472b6, #c084fc)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>Automatically.</span>
              </h3>
              <p style={{
                fontSize: '20px',
                color: 'rgba(255,255,255,0.7)',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                Vaulto backs up your Instagram content to your Google Drive.
                <br />
                Every day. Without you lifting a finger.
              </p>
            </div>

            {/* Steps */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              marginBottom: '60px'
            }}>
              {[
                { step: '1', title: 'Connect Your Instagram', desc: 'Securely link your account using Meta\'s Official API', icon: '🔗' },
                { step: '2', title: 'Link Your Google Drive', desc: 'Choose where your backups are stored—you own them', icon: '☁️' },
                { step: '3', title: 'Relax', desc: 'Automatic daily backups begin. Your content is protected.', icon: '✨' }
              ].map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '24px',
                  background: 'rgba(168, 85, 247, 0.1)',
                  border: '1px solid rgba(168, 85, 247, 0.25)',
                  borderRadius: '16px',
                  padding: '32px'
                }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.3), rgba(168, 85, 247, 0.3))',
                    borderRadius: '16px',
                    fontSize: '36px',
                    flexShrink: 0,
                    position: 'relative'
                  }}>
                    {item.icon}
                    <div style={{
                      position: 'absolute',
                      top: '-8px',
                      left: '-8px',
                      width: '28px',
                      height: '28px',
                      background: 'linear-gradient(135deg, #ec4899, #a855f7)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: '700'
                    }}>
                      {item.step}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{
                      fontSize: '22px',
                      fontWeight: '600',
                      marginBottom: '4px'
                    }}>
                      {item.title}
                    </h4>
                    <p style={{
                      fontSize: '16px',
                      color: 'rgba(255,255,255,0.6)',
                      margin: 0
                    }}>
                      {item.desc}
                    </p>
                  </div>
                  <Check size={32} color="#a855f7" style={{ flexShrink: 0 }} />
                </div>
              ))}
            </div>

            {/* Micro-FOMO message */}
            <div style={{
              marginBottom: '40px',
              padding: '24px',
              background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15), rgba(236, 72, 153, 0.1))',
              border: '1px solid rgba(168, 85, 247, 0.3)',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <p style={{
                fontSize: '16px',
                color: 'rgba(255,255,255,0.8)',
                margin: 0,
                lineHeight: '1.6'
              }}>
                💡 <strong>While others panic during a ban, your content is already safe in your Drive.</strong>
                <br />
                <span style={{ color: '#a855f7', fontSize: '14px' }}>Official Meta API — no risk of ban for using Vaulto.</span>
              </p>
            </div>

            {/* What's Backed Up */}
            <div style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '16px',
              padding: '40px',
              textAlign: 'center'
            }}>
              <h4 style={{
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '24px'
              }}>
                What Gets Backed Up
              </h4>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '16px'
              }}>
                {['Posts (Photos & Videos)', 'Reels', 'Stories Archive', 'Captions', 'Metadata'].map((item, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(168, 85, 247, 0.15))',
                    border: '1px solid rgba(168, 85, 247, 0.3)',
                    borderRadius: '100px',
                    padding: '12px 20px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    <Check size={16} color="#a855f7" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section style={{
          padding: '100px 20px',
          background: 'rgba(30, 27, 75, 0.8)'
        }}>
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 style={{
                fontSize: 'clamp(14px, 2vw, 16px)',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #f472b6, #c084fc)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '3px',
                marginBottom: '16px'
              }}>
                BUILT FOR TRUST
              </h2>
              <h3 style={{
                fontSize: 'clamp(32px, 5vw, 48px)',
                fontWeight: '700',
                lineHeight: '1.2'
              }}>
                Official Meta API.
                <br />
                Your Data. Your Control.
              </h3>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px'
            }}>
              {[
                {
                  icon: <Shield size={32} color="#a855f7" />,
                  title: 'Official Meta Integration',
                  desc: 'Uses Meta\'s official API—not scraping or workarounds. Fully compliant with Instagram\'s terms. Your account stays safe.'
                },
                {
                  icon: <Cloud size={32} color="#a855f7" />,
                  title: 'Your Data, Your Control',
                  desc: 'Backups go to YOUR Google Drive. We never store your content on third-party servers. Disconnect anytime.'
                },
                {
                  icon: <Lock size={32} color="#a855f7" />,
                  title: 'Privacy First',
                  desc: 'No access to DMs or private data. Encrypted connections. Completely transparent about what we can and cannot see.'
                }
              ].map((item, index) => (
                <div key={index} style={{
                  background: 'rgba(168, 85, 247, 0.1)',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                  borderRadius: '16px',
                  padding: '36px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2), rgba(168, 85, 247, 0.2))',
                    borderRadius: '16px',
                    margin: '0 auto 20px'
                  }}>
                    {item.icon}
                  </div>
                  <h4 style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    marginBottom: '12px'
                  }}>
                    {item.title}
                  </h4>
                  <p style={{
                    fontSize: '15px',
                    color: 'rgba(255,255,255,0.7)',
                    lineHeight: '1.6',
                    margin: 0
                  }}>
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>

            {/* API Badge */}
            <div style={{
              marginTop: '48px',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(168, 85, 247, 0.15))',
                border: '1px solid rgba(168, 85, 247, 0.4)',
                borderRadius: '100px',
                padding: '12px 24px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: 'linear-gradient(135deg, #0668E1, #1877F2)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '16px'
                }}>
                  M
                </div>
                <span style={{ fontWeight: '600' }}>Powered by Official Meta API</span>
                <Check size={20} color="#a855f7" />
              </div>
            </div>

            <CTABox text="Start Protecting Your Content" />
          </div>
        </section>

        {/* FAQ Section */}
        <section style={{
          padding: '100px 20px',
          background: 'linear-gradient(180deg, rgba(88, 28, 135, 0.4) 0%, rgba(30, 27, 75, 0.6) 100%)'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h2 style={{
                fontSize: 'clamp(14px, 2vw, 16px)',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #f472b6, #c084fc)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '3px',
                marginBottom: '16px'
              }}>
                FAQ
              </h2>
              <h3 style={{
                fontSize: 'clamp(32px, 5vw, 48px)',
                fontWeight: '700',
                lineHeight: '1.2'
              }}>
                Questions? Answers.
              </h3>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              {faqs.map((faq, index) => (
                <div key={index} className="faq-item" style={{
                  background: openFaq === index ? 'rgba(168, 85, 247, 0.1)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${openFaq === index ? 'rgba(168, 85, 247, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}>
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '24px',
                      background: 'transparent',
                      border: 'none',
                      color: '#fff',
                      fontSize: '17px',
                      fontWeight: '500',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: 'inherit'
                    }}
                  >
                    {faq.question}
                    {openFaq === index ? (
                      <ChevronUp size={20} color="#a855f7" />
                    ) : (
                      <ChevronDown size={20} color="rgba(255,255,255,0.5)" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div style={{
                      padding: '0 24px 24px',
                      fontSize: '15px',
                      color: 'rgba(255,255,255,0.7)',
                      lineHeight: '1.7'
                    }}>
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section style={{
          padding: '120px 20px 160px',
          background: 'linear-gradient(180deg, rgba(30, 27, 75, 0.7) 0%, rgba(88, 28, 135, 0.6) 50%, rgba(30, 27, 75, 0.8) 100%)',
          textAlign: 'center',
          position: 'relative'
        }}>
          {/* Glow effect */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(168, 85, 247, 0.25) 0%, transparent 70%)',
            filter: 'blur(60px)',
            pointerEvents: 'none'
          }} />

          <div style={{ maxWidth: '700px', margin: '0 auto', position: 'relative', zIndex: 10 }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(236, 72, 153, 0.2)',
              border: '1px solid rgba(236, 72, 153, 0.4)',
              borderRadius: '100px',
              padding: '8px 20px',
              marginBottom: '32px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#f9a8d4'
            }}>
              <AlertTriangle size={16} />
              17.5 MILLION ACCOUNTS EXPOSED THIS MONTH
            </div>

            <h2 style={{
              fontSize: 'clamp(36px, 6vw, 56px)',
              fontWeight: '800',
              lineHeight: '1.1',
              marginBottom: '24px'
            }}>
              Don't Wait Until
              <br />
              <span style={{
                background: 'linear-gradient(135deg, #f472b6, #c084fc)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>It's Too Late.</span>
            </h2>

            <p style={{
              fontSize: '20px',
              color: 'rgba(255,255,255,0.7)',
              marginBottom: '40px',
              lineHeight: '1.6'
            }}>
              One action today protects years of your work.
              <br />
              Your content. Your community. Your business.
            </p>

            <button onClick={handleCTA} className="cta-button" style={{
              background: 'linear-gradient(135deg, #ec4899, #a855f7)',
              border: 'none',
              borderRadius: '16px',
              padding: '24px 56px',
              fontSize: '20px',
              fontWeight: '700',
              color: '#fff',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              fontFamily: 'inherit'
            }}>
              <Zap size={24} />
              Start Now — It's Free
            </button>

            <div style={{
              marginTop: '24px',
              display: 'flex',
              justifyContent: 'center',
              gap: '32px',
              flexWrap: 'wrap',
              fontSize: '14px',
              color: 'rgba(255,255,255,0.5)'
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Check size={16} color="#a855f7" /> Free to start
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Check size={16} color="#a855f7" /> Takes 60 seconds
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Check size={16} color="#a855f7" /> Official Meta API
              </span>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer style={{
        padding: '48px 20px',
        background: 'rgba(30, 27, 75, 0.95)',
        borderTop: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px'
        }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <VaultoLogo className="h-10 w-auto" showText={true} />
          </Link>
          <div style={{
            display: 'flex',
            gap: '32px',
            fontSize: '14px',
            color: 'rgba(255,255,255,0.5)'
          }}>
            <Link to="/terms" style={{ color: 'inherit', textDecoration: 'none' }}>Terms</Link>
            <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'none' }}>Privacy</Link>
            <Link to="/contact" style={{ color: 'inherit', textDecoration: 'none' }}>Contact</Link>
          </div>
          <div style={{
            fontSize: '13px',
            color: 'rgba(255,255,255,0.3)'
          }}>
            © 2026 Vaulto. Protect your content.
          </div>
        </div>
      </footer>
    </div>
  );
}
